import {Component, HostListener, OnInit,} from '@angular/core';
import {NavigationEnd, Route, Router,} from '@angular/router';
import {PrimeNGConfig,} from 'primeng/api';
import {Logger, WindowService,} from '@services/index';
import {filter, tap,} from 'rxjs';
import {SCREEN_DEFINITIONS,} from '../app-routing.module';
import {ScreenVO,} from '@models/index';
import {pdfDefaultOptions,} from 'ngx-extended-pdf-viewer';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss',],
})
export class App implements OnInit {
  constructor(
    private primengConfig: PrimeNGConfig,
    public windowService: WindowService,
    private logger: Logger,
    private router: Router
  ) {
    // ルーティングイベント時にURLが解決できたら自動的に画面情報をWindowServiceに登録する
    router.events
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filter((event: any) => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          const screenDefs: (Route | ScreenVO)[] = SCREEN_DEFINITIONS.filter(
            (def: Route | ScreenVO) => def.path === event.urlAfterRedirects.slice(1).replace(/\?.*/, '')
          );
          if (screenDefs.length === 1) {
            const screenDef = screenDefs[0] as ScreenVO;
            //URLから画面IDを定義できたので、画面情報をセットする
            this.windowService.setScreenInfo(screenDef.screenId);
          } else if (screenDefs.length > 1) {
            this.logger.warn('CSS01999M032W', event.urlAfterRedirects);

            // 画面ID を取得するには、ID(キー) が存在するかどうかローカル ストレージをチェックする。
            // その場合は、画面ID に基づいて画面情報を設定する。
            for (const screenDef of (screenDefs as ScreenVO[])) {
              if (this.windowService.getItemFromLocalStorage<object>(screenDef.screenId) !== null) {
                this.windowService.setScreenInfo(screenDef.screenId);
                break;
              }
            }
          } else {
            if (!event.urlAfterRedirects.includes('pages/view-pdf-window')) {
              this.logger.error('CSS01999M033E', event.urlAfterRedirects);
            }
          }
        })
      )
      .subscribe();

    // PDFプレビュー画面のフォントのかすれを改善する為、ngx-extended-pdf-viewerのoptionを変更
    pdfDefaultOptions.disableFontFace = true;
  }

  ngOnInit(): void {
    this.primengConfig.ripple = true;

    if (this.windowService.hasCaller()) {
      const key = window.name + 'isChildScreen';
      const isChild: string | null = window.localStorage.getItem(key);
      if (isChild != null) {
        this.windowService.hasParentScreen = true;
        this.windowService.removeItemFromLocalStorage(key);
      }

      const callbackKey = window.name + 'callback-parent';
      const parentScreenId: string | null = window.localStorage.getItem(callbackKey);
      if (parentScreenId != null) {
        // 画面が openChild() メソッドを使用して呼び出され、コールバック関数がある場合は 親画面のscreenId に設定する。
        this.windowService.callbackParentScreenId = parentScreenId;
        this.windowService.removeItemFromLocalStorage(callbackKey);
      }
    }

    this.disableAccessKeyOnDialogDisplay();
  }

  /**
   * ダイアログが表示されている状態で、ダイアログのショートカットキーのみの動作が許可されるように、それ以外のショートカットキーを無効にする。
   * mat-dialogを表示する時に、app-rootノードにaria-hidden="true"の属性設定が追加されます。
   * その変更を監視し、画面内のaccesskeyを無効にする。
   * [依頼/QA]AP基盤 #47881
   */
   disableAccessKeyOnDialogDisplay(): void {
    const targetNode = document.getElementsByTagName('app-root')[0];
    const config = {attributes: true, childList: false, subtree: false,};

    const callback = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const targetAccesskey = 'app-root [accesskey]';
          const fixedStr = 'disabled';
          const isHidden = (mutation.target as Element).ariaHidden;

          if (isHidden === 'true') {
            // 全てのaccesskey値の先頭に固定文字を追加して、画面内のaccesskeyを無効にする
            window.document.querySelectorAll(targetAccesskey).forEach((element) => {
              if (element instanceof HTMLElement && element.accessKey.length === 1) {
                element.accessKey = fixedStr + element.accessKey;
              }
            });
          }
          else {
            // 追加した固定文字を削除して、画面内のaccesskeyを有効にする
            window.document.querySelectorAll(targetAccesskey).forEach((element) => {
              if (element instanceof HTMLElement && element.accessKey.length > 1) {
                element.accessKey = element.accessKey.replace(fixedStr, '');
              }
            });
          }
        }
      }
    };

    // コールバック関数にリンクしたオブザーバーインスタンスを生成し、対象のDOMノードの監視を開始する
    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);
  }

  @HostListener('window:beforeunload', ['$event',])
  beforeUnloadEvent(): boolean {

    const parentScreenId = this.windowService.callbackParentScreenId;

    if (parentScreenId != null) {
      if (!this.windowService.hasCaller()) {
        this.logger.debug('[beforeunload] 親画面が閉じた。');
        // falseを返却する場合、「このサイトを離れますか？」が表示される。
        return false;
      }

      const title = window.opener.document.getElementsByClassName('title');
      if (title != null && title.length > 0 && parentScreenId !== title[0].innerText.split(' : ')[0]) {
        this.logger.debug('[beforeunload] 親画面が遷移された。');
        // falseを返却する場合、「このサイトを離れますか？」が表示される。
        return false;
      }
    }

    if (this.windowService.checkFormIsDirty() || this.windowService.dbTransactionFlag || this.windowService.asyncTransactionFlag) {
      return false;
    } else {
      return true;
    }
  }
}
